import React, { useState } from 'react';
import { motion } from 'framer-motion';

const MockupPreview = () => {
  const [selectedStyle, setSelectedStyle] = useState('style1');

  const styles = [
    { id: 'style1', name: 'Style 1: Minimal Dark' },
    { id: 'style2', name: 'Style 2: Gradient Cards' },
    { id: 'style3', name: 'Style 3: Glass Morphism' },
  ];

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-white text-center mb-4">
          App Mockup Preview
        </h1>
        <p className="text-gray-400 text-center mb-8">
          Choose a style for the app mockups displayed on the website
        </p>

        {/* Style Selector */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {styles.map((style) => (
            <button
              key={style.id}
              onClick={() => setSelectedStyle(style.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                selectedStyle === style.id
                  ? 'bg-accent text-dark'
                  : 'bg-dark-100 text-gray-300 hover:bg-dark-200'
              }`}
            >
              {style.name}
            </button>
          ))}
        </div>

        {/* Business App Mockups */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            💼 Business App Mockups
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <MockupPhone
              style={selectedStyle}
              title="Dashboard"
              app="business"
              screen="dashboard"
            />
            <MockupPhone
              style={selectedStyle}
              title="Properties"
              app="business"
              screen="properties"
            />
            <MockupPhone
              style={selectedStyle}
              title="Appointments"
              app="business"
              screen="appointments"
            />
            <MockupPhone
              style={selectedStyle}
              title="AI Chat"
              app="business"
              screen="chat"
            />
          </div>
        </div>

        {/* Customer App Mockups */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            📱 Customer App Mockups
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <MockupPhone
              style={selectedStyle}
              title="Home"
              app="customer"
              screen="home"
            />
            <MockupPhone
              style={selectedStyle}
              title="Property Details"
              app="customer"
              screen="property"
            />
            <MockupPhone
              style={selectedStyle}
              title="AI Assistant"
              app="customer"
              screen="chat"
            />
            <MockupPhone
              style={selectedStyle}
              title="My Appointments"
              app="customer"
              screen="appointments"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Phone Mockup Component
const MockupPhone = ({ style, title, app, screen }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center"
    >
      <div className="relative w-48 h-[380px] rounded-[2.5rem] bg-dark-300 border-4 border-dark-400 shadow-2xl overflow-hidden">
        {/* Phone Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-dark-400 rounded-b-xl z-10" />
        
        {/* Screen Content */}
        <div className="absolute inset-2 top-6 rounded-2xl overflow-hidden">
          {style === 'style1' && <Style1Screen app={app} screen={screen} />}
          {style === 'style2' && <Style2Screen app={app} screen={screen} />}
          {style === 'style3' && <Style3Screen app={app} screen={screen} />}
        </div>
      </div>
      <p className="mt-3 text-white font-medium">{title}</p>
    </motion.div>
  );
};

// ============================================================================
// STYLE 1: Minimal Dark
// ============================================================================
const Style1Screen = ({ app, screen }) => {
  const bgColor = 'bg-[#0f0f0f]';
  
  if (app === 'business') {
    switch (screen) {
      case 'dashboard':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Dashboard</div>
            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-emerald-500/20 rounded-lg p-2">
                <div className="text-emerald-400 text-[10px]">Revenue</div>
                <div className="text-white text-sm font-bold">$12,450</div>
              </div>
              <div className="bg-blue-500/20 rounded-lg p-2">
                <div className="text-blue-400 text-[10px]">Properties</div>
                <div className="text-white text-sm font-bold">24</div>
              </div>
            </div>
            {/* Chart Placeholder */}
            <div className="bg-dark-200 rounded-lg p-2 mb-3">
              <div className="text-gray-500 text-[10px] mb-2">Weekly Overview</div>
              <div className="flex items-end justify-between h-16 gap-1">
                {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                  <div key={i} className="flex-1 bg-emerald-500/40 rounded-t" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
            {/* Recent Activity */}
            <div className="text-gray-500 text-[10px] mb-1">Recent Activity</div>
            <div className="space-y-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-dark-200 rounded-lg p-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20" />
                  <div className="flex-1">
                    <div className="h-2 bg-gray-700 rounded w-3/4" />
                    <div className="h-1.5 bg-gray-800 rounded w-1/2 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'properties':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Properties</div>
            {/* Search */}
            <div className="bg-dark-200 rounded-lg p-2 mb-3 flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-600" />
              <div className="h-2 bg-gray-700 rounded flex-1" />
            </div>
            {/* Property Cards */}
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-dark-200 rounded-lg overflow-hidden">
                  <div className="h-16 bg-gradient-to-r from-emerald-900/30 to-blue-900/30" />
                  <div className="p-2">
                    <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                    <div className="h-1.5 bg-gray-600 rounded w-1/2 mb-2" />
                    <div className="flex justify-between items-center">
                      <div className="text-emerald-400 text-[10px] font-bold">$1,200/mo</div>
                      <div className="px-2 py-0.5 bg-emerald-500/20 rounded text-emerald-400 text-[8px]">Active</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'appointments':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Appointments</div>
            {/* Calendar Mini */}
            <div className="bg-dark-200 rounded-lg p-2 mb-3">
              <div className="grid grid-cols-7 gap-1 text-center text-[8px] text-gray-500 mb-1">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[8px]">
                {Array.from({ length: 28 }, (_, i) => (
                  <div
                    key={i}
                    className={`py-1 rounded ${
                      i === 14 ? 'bg-emerald-500 text-dark' : 'text-gray-400'
                    }`}
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
            {/* Appointments List */}
            <div className="text-gray-500 text-[10px] mb-1">Today</div>
            <div className="space-y-2">
              {['9:00 AM', '2:30 PM', '5:00 PM'].map((time, i) => (
                <div key={i} className="bg-dark-200 rounded-lg p-2 flex items-center gap-2">
                  <div className="text-emerald-400 text-[10px] font-mono w-12">{time}</div>
                  <div className="flex-1">
                    <div className="h-2 bg-white/60 rounded w-3/4" />
                    <div className="h-1.5 bg-gray-700 rounded w-1/2 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'chat':
        return (
          <div className={`${bgColor} h-full p-3 flex flex-col`}>
            <div className="text-white text-xs font-semibold mb-3">AI Assistant</div>
            <div className="flex-1 space-y-2 overflow-hidden">
              {/* AI Message */}
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-[8px] text-dark font-bold">AI</div>
                <div className="bg-dark-200 rounded-lg rounded-tl-none p-2 max-w-[80%]">
                  <div className="text-[10px] text-gray-300">Hello! How can I help you manage your properties today?</div>
                </div>
              </div>
              {/* User Message */}
              <div className="flex gap-2 justify-end">
                <div className="bg-emerald-500/20 rounded-lg rounded-tr-none p-2 max-w-[80%]">
                  <div className="text-[10px] text-emerald-300">Show me today's appointments</div>
                </div>
              </div>
              {/* AI Message */}
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-[8px] text-dark font-bold">AI</div>
                <div className="bg-dark-200 rounded-lg rounded-tl-none p-2 max-w-[80%]">
                  <div className="text-[10px] text-gray-300">You have 3 appointments scheduled for today...</div>
                </div>
              </div>
            </div>
            {/* Input */}
            <div className="mt-2 bg-dark-200 rounded-full p-2 flex items-center gap-2">
              <div className="h-2 bg-gray-700 rounded flex-1" />
              <div className="w-6 h-6 rounded-full bg-emerald-500" />
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  // Customer App Screens
  switch (screen) {
    case 'home':
      return (
        <div className={`${bgColor} h-full p-3`}>
          <div className="text-white text-xs font-semibold mb-3">Find Your Home</div>
          {/* Search */}
          <div className="bg-dark-200 rounded-lg p-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500/30" />
              <div className="h-2 bg-gray-700 rounded flex-1" />
            </div>
          </div>
          {/* Categories */}
          <div className="flex gap-2 mb-3 overflow-hidden">
            {['Apartment', 'House', 'Studio'].map((cat, i) => (
              <div key={i} className={`px-2 py-1 rounded-full text-[8px] whitespace-nowrap ${i === 0 ? 'bg-emerald-500 text-dark' : 'bg-dark-200 text-gray-400'}`}>
                {cat}
              </div>
            ))}
          </div>
          {/* Featured */}
          <div className="text-gray-500 text-[10px] mb-1">Featured</div>
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="bg-dark-200 rounded-lg overflow-hidden">
                <div className="h-20 bg-gradient-to-br from-purple-900/30 to-emerald-900/30 relative">
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-dark/50 flex items-center justify-center">
                    <div className="text-[8px]">♥</div>
                  </div>
                </div>
                <div className="p-2">
                  <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                  <div className="flex items-center gap-1 mb-1">
                    <div className="text-emerald-400 text-[10px] font-bold">$950/mo</div>
                    <div className="text-gray-500 text-[8px]">• 2 bed • 1 bath</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'property':
      return (
        <div className={`${bgColor} h-full`}>
          {/* Image */}
          <div className="h-32 bg-gradient-to-br from-blue-900/40 to-purple-900/40 relative">
            <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-dark/50" />
            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-dark/50" />
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 1 ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </div>
          <div className="p-3">
            <div className="text-white text-sm font-semibold mb-1">Modern Apartment</div>
            <div className="text-gray-500 text-[10px] mb-2">123 Main St, Downtown</div>
            <div className="flex gap-3 mb-3">
              {[{ label: '2 Bed', icon: '🛏' }, { label: '1 Bath', icon: '🚿' }, { label: '850 sqft', icon: '📐' }].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-sm">{item.icon}</div>
                  <div className="text-[8px] text-gray-400">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="text-emerald-400 text-lg font-bold mb-2">$1,200/mo</div>
            <div className="bg-emerald-500 rounded-lg py-2 text-center text-dark text-xs font-semibold">
              Schedule Visit
            </div>
          </div>
        </div>
      );
    case 'chat':
      return (
        <div className={`${bgColor} h-full p-3 flex flex-col`}>
          <div className="text-white text-xs font-semibold mb-3">AI Assistant</div>
          <div className="flex-1 space-y-2 overflow-hidden">
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-[8px] text-white font-bold">AI</div>
              <div className="bg-dark-200 rounded-lg rounded-tl-none p-2 max-w-[80%]">
                <div className="text-[10px] text-gray-300">Hi! I can help you find your perfect home. What are you looking for?</div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <div className="bg-purple-500/20 rounded-lg rounded-tr-none p-2 max-w-[80%]">
                <div className="text-[10px] text-purple-300">2 bedroom apartment under $1500</div>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-[8px] text-white font-bold">AI</div>
              <div className="bg-dark-200 rounded-lg rounded-tl-none p-2 max-w-[80%]">
                <div className="text-[10px] text-gray-300">I found 12 properties matching your criteria!</div>
              </div>
            </div>
          </div>
          <div className="mt-2 bg-dark-200 rounded-full p-2 flex items-center gap-2">
            <div className="h-2 bg-gray-700 rounded flex-1" />
            <div className="w-6 h-6 rounded-full bg-purple-500" />
          </div>
        </div>
      );
    case 'appointments':
      return (
        <div className={`${bgColor} h-full p-3`}>
          <div className="text-white text-xs font-semibold mb-3">My Appointments</div>
          {/* Tabs */}
          <div className="flex gap-2 mb-3">
            <div className="px-3 py-1 bg-emerald-500 rounded-full text-[10px] text-dark font-medium">Upcoming</div>
            <div className="px-3 py-1 bg-dark-200 rounded-full text-[10px] text-gray-400">Past</div>
          </div>
          {/* Appointments */}
          <div className="space-y-2">
            {[
              { date: 'Today', time: '2:00 PM', status: 'Confirmed' },
              { date: 'Tomorrow', time: '10:00 AM', status: 'Pending' },
              { date: 'Jan 20', time: '3:30 PM', status: 'Confirmed' },
            ].map((apt, i) => (
              <div key={i} className="bg-dark-200 rounded-lg p-2">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <div className="h-2 bg-white/80 rounded w-24 mb-1" />
                    <div className="h-1.5 bg-gray-700 rounded w-16" />
                  </div>
                  <div className={`px-2 py-0.5 rounded text-[8px] ${apt.status === 'Confirmed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {apt.status}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="text-gray-500 text-[10px]">{apt.date}</div>
                  <div className="text-emerald-400 text-[10px] font-medium">{apt.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

// ============================================================================
// STYLE 2: Gradient Cards
// ============================================================================
const Style2Screen = ({ app, screen }) => {
  const bgGradient = app === 'business' 
    ? 'bg-gradient-to-b from-emerald-950 to-gray-950' 
    : 'bg-gradient-to-b from-purple-950 to-gray-950';
  // eslint-disable-next-line no-unused-vars
  const accent = app === 'business' ? 'emerald' : 'purple';
  
  if (app === 'business') {
    switch (screen) {
      case 'dashboard':
        return (
          <div className={`${bgGradient} h-full p-3`}>
            <div className="flex justify-between items-center mb-3">
              <div className="text-white text-xs font-semibold">Dashboard</div>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600" />
            </div>
            {/* Revenue Card */}
            <div className="bg-gradient-to-br from-emerald-500/30 to-emerald-700/20 rounded-xl p-3 mb-3 border border-emerald-500/30">
              <div className="text-emerald-300 text-[10px]">Total Revenue</div>
              <div className="text-white text-xl font-bold">$24,580</div>
              <div className="text-emerald-400 text-[10px]">↑ 12% from last month</div>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                <div className="text-gray-400 text-[10px]">Properties</div>
                <div className="text-white text-sm font-bold">24</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                <div className="text-gray-400 text-[10px]">Bookings</div>
                <div className="text-white text-sm font-bold">18</div>
              </div>
            </div>
            {/* Quick Actions */}
            <div className="flex gap-2">
              <div className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg py-2 text-center text-[10px] text-dark font-semibold">+ Add Property</div>
            </div>
          </div>
        );
      case 'properties':
        return (
          <div className={`${bgGradient} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Properties</div>
            <div className="space-y-2">
              {[
                { color: 'from-emerald-500/40 to-teal-600/40' },
                { color: 'from-blue-500/40 to-indigo-600/40' },
                { color: 'from-orange-500/40 to-red-600/40' },
              ].map((item, i) => (
                <div key={i} className={`bg-gradient-to-r ${item.color} rounded-xl p-3 border border-white/10`}>
                  <div className="flex gap-2">
                    <div className="w-12 h-12 rounded-lg bg-white/10" />
                    <div className="flex-1">
                      <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                      <div className="h-1.5 bg-white/40 rounded w-1/2 mb-2" />
                      <div className="text-emerald-300 text-[10px] font-bold">$1,450/mo</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'appointments':
        return (
          <div className={`${bgGradient} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Schedule</div>
            {/* Today Highlight */}
            <div className="bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-xl p-3 mb-3 border border-emerald-400/30">
              <div className="text-emerald-300 text-[10px] mb-1">Today, Jan 13</div>
              <div className="text-white text-sm font-semibold">3 Appointments</div>
            </div>
            {/* Timeline */}
            <div className="space-y-2">
              {['9:00 AM', '2:00 PM', '5:30 PM'].map((time, i) => (
                <div key={i} className="flex gap-2">
                  <div className="text-emerald-400 text-[10px] font-mono w-12">{time}</div>
                  <div className="flex-1 bg-white/5 rounded-lg p-2 border-l-2 border-emerald-500">
                    <div className="h-2 bg-white/60 rounded w-3/4 mb-1" />
                    <div className="h-1.5 bg-white/30 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'chat':
        return (
          <div className={`${bgGradient} h-full p-3 flex flex-col`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-[10px] text-dark font-bold">AI</div>
              <div>
                <div className="text-white text-xs font-semibold">EzDaily Assistant</div>
                <div className="text-emerald-400 text-[8px]">● Online</div>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="bg-white/10 rounded-xl rounded-tl-none p-2 max-w-[85%]">
                <div className="text-[10px] text-gray-200">How can I assist with your business today?</div>
              </div>
              <div className="flex justify-end">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl rounded-tr-none p-2 max-w-[85%]">
                  <div className="text-[10px] text-dark">Generate property description</div>
                </div>
              </div>
            </div>
            <div className="mt-2 bg-white/10 rounded-full p-2 flex items-center gap-2 border border-white/20">
              <div className="h-2 bg-white/30 rounded flex-1" />
              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" />
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  // Customer App with gradient style
  switch (screen) {
    case 'home':
      return (
        <div className={`${bgGradient} h-full p-3`}>
          <div className="flex justify-between items-center mb-3">
            <div className="text-white text-xs font-semibold">Discover</div>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600" />
          </div>
          <div className="bg-white/10 rounded-xl p-2 mb-3 border border-white/20">
            <div className="flex items-center gap-2">
              <div className="text-purple-400">🔍</div>
              <div className="h-2 bg-white/30 rounded flex-1" />
            </div>
          </div>
          <div className="text-gray-400 text-[10px] mb-2">Near You</div>
          <div className="space-y-2">
            {[
              { grad: 'from-purple-500/40 to-pink-600/40' },
              { grad: 'from-blue-500/40 to-purple-600/40' },
            ].map((item, i) => (
              <div key={i} className={`bg-gradient-to-r ${item.grad} rounded-xl overflow-hidden border border-white/10`}>
                <div className="h-16 bg-white/5" />
                <div className="p-2">
                  <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                  <div className="text-purple-300 text-[10px] font-bold">$1,100/mo</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'property':
      return (
        <div className={`${bgGradient} h-full`}>
          <div className="h-28 bg-gradient-to-br from-purple-500/30 to-pink-500/30 relative">
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 1 ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </div>
          <div className="p-3">
            <div className="text-white text-sm font-semibold mb-1">Luxury Studio</div>
            <div className="text-gray-400 text-[10px] mb-3">Downtown District</div>
            <div className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-xl p-2 mb-3 border border-purple-400/30">
              <div className="text-purple-300 text-xl font-bold">$1,350/mo</div>
              <div className="text-gray-400 text-[10px]">Utilities included</div>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl py-2 text-center text-white text-xs font-semibold">
              Book a Visit
            </div>
          </div>
        </div>
      );
    case 'chat':
      return (
        <div className={`${bgGradient} h-full p-3 flex flex-col`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-[10px] text-white font-bold">AI</div>
            <div>
              <div className="text-white text-xs font-semibold">Home Finder</div>
              <div className="text-purple-400 text-[8px]">● Ready to help</div>
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <div className="bg-white/10 rounded-xl rounded-tl-none p-2 max-w-[85%]">
              <div className="text-[10px] text-gray-200">Let me help you find your perfect home!</div>
            </div>
            <div className="flex justify-end">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl rounded-tr-none p-2 max-w-[85%]">
                <div className="text-[10px] text-white">Pet-friendly apartments?</div>
              </div>
            </div>
          </div>
          <div className="mt-2 bg-white/10 rounded-full p-2 flex items-center gap-2 border border-white/20">
            <div className="h-2 bg-white/30 rounded flex-1" />
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-400 to-pink-500" />
          </div>
        </div>
      );
    case 'appointments':
      return (
        <div className={`${bgGradient} h-full p-3`}>
          <div className="text-white text-xs font-semibold mb-3">Visits</div>
          <div className="bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-xl p-3 mb-3 border border-purple-400/30">
            <div className="text-purple-300 text-[10px]">Upcoming</div>
            <div className="text-white text-lg font-bold">2 Scheduled</div>
          </div>
          <div className="space-y-2">
            {['Tomorrow, 10 AM', 'Jan 18, 3 PM'].map((date, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-2 border border-white/10">
                <div className="h-2 bg-white/60 rounded w-3/4 mb-1" />
                <div className="text-purple-300 text-[10px]">{date}</div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

// ============================================================================
// STYLE 3: Glass Morphism
// ============================================================================
const Style3Screen = ({ app, screen }) => {
  const bgColor = app === 'business' ? 'bg-emerald-950' : 'bg-purple-950';
  // eslint-disable-next-line no-unused-vars
  const accent = app === 'business' ? 'emerald' : 'purple';
  
  const GlassCard = ({ children, className = '' }) => (
    <div className={`bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 ${className}`}>
      {children}
    </div>
  );

  if (app === 'business') {
    switch (screen) {
      case 'dashboard':
        return (
          <div className={`${bgColor} h-full p-3 relative overflow-hidden`}>
            {/* Background blur circles */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/30 rounded-full blur-2xl" />
            <div className="absolute bottom-10 left-0 w-16 h-16 bg-teal-500/30 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="text-white text-xs font-semibold mb-3">Dashboard</div>
              <GlassCard className="p-3 mb-3">
                <div className="text-emerald-300 text-[10px]">Revenue</div>
                <div className="text-white text-lg font-bold">$18,420</div>
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="w-3/4 h-full bg-emerald-400 rounded-full" />
                  </div>
                  <div className="text-[8px] text-emerald-300">75%</div>
                </div>
              </GlassCard>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <GlassCard className="p-2">
                  <div className="text-[10px] text-gray-300">Properties</div>
                  <div className="text-white text-sm font-bold">32</div>
                </GlassCard>
                <GlassCard className="p-2">
                  <div className="text-[10px] text-gray-300">Tenants</div>
                  <div className="text-white text-sm font-bold">48</div>
                </GlassCard>
              </div>
              <GlassCard className="p-2">
                <div className="text-[10px] text-gray-300 mb-2">Activity</div>
                <div className="space-y-1">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/30" />
                      <div className="flex-1 h-2 bg-white/20 rounded" />
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </div>
        );
      case 'properties':
        return (
          <div className={`${bgColor} h-full p-3 relative overflow-hidden`}>
            <div className="absolute top-10 left-0 w-24 h-24 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-white text-xs font-semibold mb-3">Properties</div>
              <GlassCard className="p-2 mb-3 flex items-center gap-2">
                <div className="text-gray-400">🔍</div>
                <div className="h-2 bg-white/20 rounded flex-1" />
              </GlassCard>
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <GlassCard key={i} className="p-2 flex gap-2">
                    <div className="w-14 h-14 rounded-lg bg-white/10" />
                    <div className="flex-1">
                      <div className="h-2 bg-white/60 rounded w-3/4 mb-1" />
                      <div className="h-1.5 bg-white/30 rounded w-1/2 mb-2" />
                      <div className="text-emerald-300 text-[10px] font-bold">$1,200/mo</div>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          </div>
        );
      case 'appointments':
        return (
          <div className={`${bgColor} h-full p-3 relative overflow-hidden`}>
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-teal-500/30 rounded-full blur-2xl" />
            <div className="relative">
              <div className="text-white text-xs font-semibold mb-3">Schedule</div>
              <GlassCard className="p-2 mb-3">
                <div className="grid grid-cols-7 gap-1 text-[8px] text-center">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div key={i} className="text-gray-400">{d}</div>
                  ))}
                  {Array.from({ length: 21 }, (_, i) => (
                    <div key={i} className={`py-0.5 rounded ${i === 12 ? 'bg-emerald-500 text-dark' : 'text-gray-300'}`}>
                      {i + 1}
                    </div>
                  ))}
                </div>
              </GlassCard>
              <div className="space-y-2">
                {['10:00 AM', '2:30 PM'].map((time, i) => (
                  <GlassCard key={i} className="p-2 flex items-center gap-2">
                    <div className="text-emerald-300 text-[10px] font-mono">{time}</div>
                    <div className="flex-1 h-2 bg-white/30 rounded" />
                  </GlassCard>
                ))}
              </div>
            </div>
          </div>
        );
      case 'chat':
        return (
          <div className={`${bgColor} h-full p-3 relative overflow-hidden flex flex-col`}>
            <div className="absolute top-0 left-0 w-16 h-16 bg-emerald-500/30 rounded-full blur-2xl" />
            <div className="relative flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <GlassCard className="w-8 h-8 flex items-center justify-center">
                  <span className="text-[10px]">🤖</span>
                </GlassCard>
                <div className="text-white text-xs font-semibold">AI Assistant</div>
              </div>
              <div className="flex-1 space-y-2">
                <GlassCard className="p-2 max-w-[85%]">
                  <div className="text-[10px] text-gray-200">How can I help you?</div>
                </GlassCard>
                <div className="flex justify-end">
                  <div className="bg-emerald-500/30 backdrop-blur-sm rounded-xl border border-emerald-400/30 p-2 max-w-[85%]">
                    <div className="text-[10px] text-emerald-200">Check vacant units</div>
                  </div>
                </div>
              </div>
              <GlassCard className="mt-2 p-2 flex items-center gap-2">
                <div className="h-2 bg-white/20 rounded flex-1" />
                <div className="w-5 h-5 rounded-full bg-emerald-500/50" />
              </GlassCard>
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  // Customer App Glass Style
  switch (screen) {
    case 'home':
      return (
        <div className={`${bgColor} h-full p-3 relative overflow-hidden`}>
          <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-16 h-16 bg-pink-500/30 rounded-full blur-2xl" />
          <div className="relative">
            <div className="text-white text-xs font-semibold mb-3">Find Home</div>
            <GlassCard className="p-2 mb-3 flex items-center gap-2">
              <div className="text-purple-300">🔍</div>
              <div className="h-2 bg-white/20 rounded flex-1" />
            </GlassCard>
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <GlassCard key={i} className="overflow-hidden">
                  <div className="h-16 bg-white/5" />
                  <div className="p-2">
                    <div className="h-2 bg-white/60 rounded w-3/4 mb-1" />
                    <div className="text-purple-300 text-[10px] font-bold">$980/mo</div>
                  </div>
                </GlassCard>
              ))}
            </div>
          </div>
        </div>
      );
    case 'property':
      return (
        <div className={`${bgColor} h-full relative overflow-hidden`}>
          <div className="absolute top-20 right-0 w-20 h-20 bg-pink-500/30 rounded-full blur-3xl" />
          <div className="h-28 bg-white/5" />
          <div className="p-3 relative">
            <GlassCard className="p-3 mb-3 -mt-6">
              <div className="text-white text-sm font-semibold">Urban Loft</div>
              <div className="text-gray-400 text-[10px] mb-2">Arts District</div>
              <div className="text-purple-300 text-lg font-bold">$1,450/mo</div>
            </GlassCard>
            <GlassCard className="p-2">
              <div className="text-center text-white text-xs font-semibold">Schedule Tour</div>
            </GlassCard>
          </div>
        </div>
      );
    case 'chat':
      return (
        <div className={`${bgColor} h-full p-3 relative overflow-hidden flex flex-col`}>
          <div className="absolute bottom-10 right-0 w-20 h-20 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="relative flex-1 flex flex-col">
            <GlassCard className="p-2 mb-3 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500/30 flex items-center justify-center text-[10px]">🏠</div>
              <div className="text-white text-xs">Home Finder AI</div>
            </GlassCard>
            <div className="flex-1 space-y-2">
              <GlassCard className="p-2 max-w-[85%]">
                <div className="text-[10px] text-gray-200">What's your ideal neighborhood?</div>
              </GlassCard>
            </div>
            <GlassCard className="mt-2 p-2 flex items-center gap-2">
              <div className="h-2 bg-white/20 rounded flex-1" />
              <div className="w-5 h-5 rounded-full bg-purple-500/50" />
            </GlassCard>
          </div>
        </div>
      );
    case 'appointments':
      return (
        <div className={`${bgColor} h-full p-3 relative overflow-hidden`}>
          <div className="absolute top-0 left-0 w-20 h-20 bg-pink-500/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="text-white text-xs font-semibold mb-3">My Visits</div>
            <GlassCard className="p-3 mb-3">
              <div className="text-purple-300 text-[10px]">Next Visit</div>
              <div className="text-white text-sm font-bold">Tomorrow, 2 PM</div>
            </GlassCard>
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <GlassCard key={i} className="p-2">
                  <div className="h-2 bg-white/50 rounded w-3/4 mb-1" />
                  <div className="h-1.5 bg-white/30 rounded w-1/2" />
                </GlassCard>
              ))}
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

export default MockupPreview;
